import apiClient from './apiClient';
import { handleApiError } from './utils/apiErrorUtils';

// Cache preference interfaces
export interface CachePreference {
  id: number;
  uuid: string;
  platform: number | null;
  platform_name: string | null;
  platform_type: string | null;
  cache_enabled: boolean;
  cache_duration_days: number;
  created_at: string;
  updated_at: string;
}

export interface GlobalCacheSettingsResponse {
  preference: CachePreference;
  created: boolean;
}

export interface UpdateGlobalCacheSettingsRequest {
  cache_enabled: boolean;
  cache_duration_days: number;
}

export interface UpdateGlobalCacheSettingsResponse {
  preference: CachePreference;
  created: boolean;
  message: string;
}

export interface PlatformCacheSetting {
  platform_id: number;
  platform_name: string;
  platform_type: string;
  has_custom_settings: boolean;
  cache_enabled: boolean;
  cache_duration_days: number;
}

export interface PlatformCacheSettingsResponse {
  platform_preferences: PlatformCacheSetting[];
  total_platforms: number;
}

export interface UpdatePlatformCacheSettingsRequest {
  platform_id: number;
  cache_enabled: boolean;
  cache_duration_days: number;
}

export interface UpdatePlatformCacheSettingsResponse {
  preference: CachePreference;
  created: boolean;
  message: string;
}

export interface RemovePlatformCacheSettingsRequest {
  platform_id: number;
}

export interface RemovePlatformCacheSettingsResponse {
  message: string;
  deleted: boolean;
}

export interface PlatformCacheStatistic {
  platform_id: number;
  platform_name: string;
  platform_type: string;
  total_cached_items: number;
  fresh_items: number;
  expired_items: number;
  cache_hit_potential: string;
  cache_duration_days: number;
}

export interface GlobalCacheStatistics {
  total_cached_items: number;
  total_fresh_items: number;
  overall_hit_potential: string;
}

export interface CacheStatisticsResponse {
  platform_statistics: PlatformCacheStatistic[];
  global_statistics: GlobalCacheStatistics;
}

export interface CachePreferencesListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: CachePreference[];
}

export interface CreateCachePreferenceRequest {
  platform?: number;
  cache_enabled: boolean;
  cache_duration_days: number;
}

class CachePreferencesService {
  // Global cache settings
  async getGlobalSettings(): Promise<GlobalCacheSettingsResponse> {
    try {
      const response = await apiClient.get<GlobalCacheSettingsResponse>('/api/cache-preferences/global_settings/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateGlobalSettings(data: UpdateGlobalCacheSettingsRequest): Promise<UpdateGlobalCacheSettingsResponse> {
    try {
      const response = await apiClient.post<UpdateGlobalCacheSettingsResponse>(
        '/api/cache-preferences/update_global_settings/',
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Platform cache settings
  async getPlatformSettings(): Promise<PlatformCacheSettingsResponse> {
    try {
      const response = await apiClient.get<PlatformCacheSettingsResponse>('/api/cache-preferences/platform_settings/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updatePlatformSettings(data: UpdatePlatformCacheSettingsRequest): Promise<UpdatePlatformCacheSettingsResponse> {
    try {
      const response = await apiClient.post<UpdatePlatformCacheSettingsResponse>(
        '/api/cache-preferences/update_platform_settings/',
        data
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async removePlatformSettings(data: RemovePlatformCacheSettingsRequest): Promise<RemovePlatformCacheSettingsResponse> {
    try {
      const response = await apiClient.delete<RemovePlatformCacheSettingsResponse>(
        '/api/cache-preferences/remove_platform_settings/',
        { data }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Cache statistics
  async getCacheStatistics(): Promise<CacheStatisticsResponse> {
    try {
      const response = await apiClient.get<CacheStatisticsResponse>('/api/cache-preferences/cache_statistics/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Standard CRUD operations
  async getAllCachePreferences(): Promise<CachePreferencesListResponse> {
    try {
      const response = await apiClient.get<CachePreferencesListResponse>('/api/cache-preferences/');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async createCachePreference(data: CreateCachePreferenceRequest): Promise<CachePreference> {
    try {
      const response = await apiClient.post<CachePreference>('/api/cache-preferences/', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getCachePreference(id: number): Promise<CachePreference> {
    try {
      const response = await apiClient.get<CachePreference>(`/api/cache-preferences/${id}/`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateCachePreference(id: number, data: Partial<CreateCachePreferenceRequest>): Promise<CachePreference> {
    try {
      const response = await apiClient.put<CachePreference>(`/api/cache-preferences/${id}/`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deleteCachePreference(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/cache-preferences/${id}/`);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Utility methods
  async getEffectiveCacheSettings(platformId?: number): Promise<{ cache_enabled: boolean; cache_duration_days: number }> {
    try {
      if (platformId) {
        // Try to get platform-specific settings first
        const platformSettings = await this.getPlatformSettings();
        const platformSetting = platformSettings.platform_preferences.find(p => p.platform_id === platformId);
        
        if (platformSetting && platformSetting.has_custom_settings) {
          return {
            cache_enabled: platformSetting.cache_enabled,
            cache_duration_days: platformSetting.cache_duration_days
          };
        }
      }

      // Fall back to global settings
      const globalSettings = await this.getGlobalSettings();
      return {
        cache_enabled: globalSettings.preference.cache_enabled,
        cache_duration_days: globalSettings.preference.cache_duration_days
      };
    } catch (error) {
      // Return default settings if API calls fail
      console.error('Failed to get cache settings, using defaults:', error);
      return {
        cache_enabled: true,
        cache_duration_days: 7
      };
    }
  }
}

export const cachePreferencesService = new CachePreferencesService();